import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Avatar, Box, Card, CircularProgress, Divider, FormControlLabel,
  InputAdornment, Stack, Switch, TextField, Typography, Chip,
} from '@mui/material';
import {
  Search as SearchIcon, PersonOutline as PersonIcon,
  LocalShippingOutlined as TruckIcon,
} from '@mui/icons-material';
import DocumentService, { CategoryCount, RosterRow } from '../../api/services/document.service';
import { CoverageMeter, ObligationPill } from '../../components/files/vaultUI';
import FilesShell, { FileDocumentButton } from '../../components/files/FilesShell';
import PageHeader from '../../components/ui/PageHeader';

// The people list or the truck list, each row carrying the state of that
// owner's paperwork. Only drivers and vehicles have rosters — the company
// has one file, and an accident case is reached from its own record.

const RosterPage: React.FC = () => {
  const navigate = useNavigate();
  const { ownerType } = useParams<{ ownerType: string }>();
  const kind: 'driver' | 'vehicle' = ownerType === 'vehicle' ? 'vehicle' : 'driver';

  const [rows, setRows] = useState<RosterRow[]>([]);
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rosterRows, categoryList] = await Promise.all([
        DocumentService.getRoster(kind, includeInactive),
        DocumentService.getCategories().catch(() => [] as CategoryCount[]),
      ]);
      setRows(rosterRows);
      setCategories(categoryList);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that list');
    } finally {
      setLoading(false);
    }
  }, [kind, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      row.label.toLowerCase().includes(term) || (row.sublabel || '').toLowerCase().includes(term));
  }, [rows, search]);

  return (
    <FilesShell categories={categories}
      onUploaded={load}
      header={
        <PageHeader
          title={kind === 'vehicle' ? 'Vehicles' : 'Drivers & staff'}
          subtitle={`${rows.length} ${kind === 'vehicle' ? 'in the fleet' : 'on the roster'} · open one to see their paperwork`}
          actions={
            <>
              <FormControlLabel
                control={<Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
                label={kind === 'vehicle' ? 'Show retired' : 'Show inactive'}
              />
              <FileDocumentButton />
            </>
          }
        />
      }
    >

      <TextField
        fullWidth
        size="small"
        placeholder={kind === 'vehicle' ? 'Find a truck…' : 'Find someone…'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        sx={{ my: 2 }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Card>
          {visible.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {rows.length === 0
                  ? `No ${kind === 'vehicle' ? 'vehicles' : 'people'} yet.`
                  : 'Nothing matches that search.'}
              </Typography>
            </Box>
          ) : (
            visible.map((row, index) => (
              <React.Fragment key={row.ownerId}>
                {index > 0 && <Divider />}
                <Box
                  onClick={() => navigate(`/files/entity/${row.ownerType}/${row.ownerId}`)}
                  sx={{
                    px: 2, py: 1.5, cursor: 'pointer',
                    opacity: row.inactive ? 0.6 : 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={2}>
                    <Avatar
                      sx={{
                        width: 34, height: 34, bgcolor: 'primary.light', color: 'primary.dark', fontSize: 14, fontWeight: 700,
                      }}
                    >
                      {kind === 'vehicle' ? <TruckIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="body2" fontWeight={700} noWrap>{row.label}</Typography>
                        {row.inactive && (
                          <Chip size="small" variant="outlined" label={kind === 'vehicle' ? 'Retired' : 'Inactive'} />
                        )}
                      </Stack>
                      {row.sublabel && (
                        <Typography variant="caption" color="text.secondary" noWrap>{row.sublabel}</Typography>
                      )}
                    </Box>
                    {/* score is null when this owner has no obligations —
                        the meter renders "Not tracked", never 100%. */}
                    <CoverageMeter score={row.score} />
                    {row.worstStatus && <ObligationPill status={row.worstStatus} />}
                  </Stack>
                </Box>
              </React.Fragment>
            ))
          )}
        </Card>
      )}
    </FilesShell>
  );
};

export default RosterPage;
